import clsx from 'clsx';

export interface MetricData {
  label: string;
  /** null = NOT MEASURED. Rendered as "Not measured", never as 0%. */
  value: number | null;
  target?: number;
  unit?: string;
  /** Optional explanation shown when the metric is unavailable. */
  unavailableNote?: string;
}

export interface RealTimeMetricsCardProps {
  metrics?: MetricData[];
  isRecording?: boolean;
  hasSession?: boolean;
  className?: string;
}

const DEFAULT_METRICS: MetricData[] = [
  { label: 'Clarity', value: null, target: 85 },
  { label: 'Pace', value: null, target: 80 },
  { label: 'Confidence', value: null, target: 75 },
  { label: 'Energy', value: null, target: 80 },
];

/** Clamp to 0-100, preserving "not measured" as null (never coercing it to 0). */
function clampOrNull(value?: number | null): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, value));
}

export function RealTimeMetricsCard({
  metrics = DEFAULT_METRICS,
  isRecording = false,
  hasSession = false,
  className,
}: RealTimeMetricsCardProps) {
  // Ensure metrics is always a valid array
  const safeMetrics = Array.isArray(metrics) ? metrics : DEFAULT_METRICS;

  // Determine display state
  const hasNonZeroMetrics = safeMetrics.some((m) => (clampOrNull(m.value) ?? 0) > 0);
  const showPlaceholder = !hasSession && !isRecording && !hasNonZeroMetrics;

  return (
    <div className={clsx('card', className)}>
      <div className="card-header flex items-center justify-between">
        {/* R0.1: "Real-Time Metrics" overstated it — on the record & review path
            these appear after the recording is processed, not live. */}
        <h3 className="font-semibold text-gray-900">Delivery Metrics</h3>
        {isRecording ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
            <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
            Recording
          </span>
        ) : hasNonZeroMetrics ? (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
            Complete
          </span>
        ) : null}
      </div>
      <div className="card-body">
        {showPlaceholder ? (
          <PlaceholderState />
        ) : (
          <div className="space-y-4">
            {safeMetrics.map((metric) => (
              <MetricItem key={metric.label} {...metric} isActive={isRecording} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PlaceholderState() {
  return (
    <div className="text-center py-6">
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
        <svg
          className="w-6 h-6 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      </div>
      <p className="text-sm text-gray-500">
        Tap the microphone to start practicing.
      </p>
      <p className="text-xs text-gray-400 mt-1">
        Your metrics will appear here once your practice is analyzed.
      </p>
    </div>
  );
}

interface MetricItemProps extends MetricData {
  isActive?: boolean;
}

function MetricItem({
  label,
  value,
  target,
  unit = '%',
  isActive = false,
  unavailableNote,
}: MetricItemProps) {
  const clampedValue = clampOrNull(value);

  // NOT MEASURED: render an explicit statement with no bar and no number. A 0% bar
  // reads as "you scored zero" when it actually means "we did not measure this".
  if (clampedValue === null) {
    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">{label ?? 'Metric'}</span>
          <span className="text-sm text-gray-400 italic">Not measured</span>
        </div>
        {unavailableNote && <p className="text-xs text-gray-400">{unavailableNote}</p>}
      </div>
    );
  }

  const displayValue = Math.round(clampedValue);

  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score === 0) return 'text-gray-400';
    return 'text-orange-500';
  };

  const getProgressColor = (score: number): string => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score === 0) return 'bg-gray-200';
    return 'bg-orange-500';
  };

  const getTargetStatus = (score: number, targetValue?: number): string | null => {
    if (targetValue === undefined || score === 0) return null;
    if (score >= targetValue) return 'Target met!';
    const diff = targetValue - score;
    if (diff <= 10) return 'Almost there';
    return null;
  };

  const targetStatus = getTargetStatus(displayValue, target);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{label ?? 'Metric'}</span>
        <div className="flex items-center gap-2">
          {targetStatus && (
            <span className="text-xs text-green-600">{targetStatus}</span>
          )}
          <span
            className={clsx(
              'text-lg font-bold tabular-nums min-w-[3.5rem] text-right',
              getScoreColor(displayValue),
              isActive && displayValue > 0 && 'transition-all duration-300'
            )}
          >
            {displayValue}{unit}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
        {/* Target marker */}
        {target !== undefined && (
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-gray-400 z-10"
            style={{ left: `${Math.min(target, 100)}%` }}
            title={`Target: ${target}%`}
          />
        )}

        {/* Progress fill */}
        <div
          className={clsx(
            'h-full rounded-full',
            getProgressColor(displayValue),
            isActive && 'transition-all duration-300'
          )}
          style={{ width: `${clampedValue}%` }}
        />
      </div>

      {/* Target label */}
      {target !== undefined && (
        <div className="flex justify-between text-xs text-gray-400">
          <span>0%</span>
          <span>Target: {target}%</span>
          <span>100%</span>
        </div>
      )}
    </div>
  );
}

// Pre-configured metrics display for practice sessions
export function PracticeMetrics({ isRecording = false }: { isRecording?: boolean }) {
  const metrics: MetricData[] = [
    { label: 'Clarity', value: 78, target: 85 },
    { label: 'Pace', value: 82, target: 80 },
    { label: 'Confidence', value: 70, target: 75 },
    { label: 'Energy', value: 85, target: 80 },
  ];

  return <RealTimeMetricsCard metrics={metrics} isRecording={isRecording} hasSession={true} />;
}
